/**
 * Tiny reactive store. Typed event-emitter pattern — no Redux, no Zustand.
 * Game developers can extend this by creating new stores:
 *
 *   const inventory = createStore({ items: [] as Item[] });
 *   inventory.subscribe((s) => renderInventory(s.items));
 *   inventory.set((s) => ({ items: [...s.items, newItem] }));
 */
export type Updater<T> = (prev: T) => T;
export type Listener<T> = (state: T) => void;

export interface Store<T> {
  get(): T;
  set(next: T | Updater<T>): void;
  subscribe(fn: Listener<T>): () => void;
}

export function createStore<T>(initial: T): Store<T> {
  let state = initial;
  const listeners = new Set<Listener<T>>();

  return {
    get: () => state,
    set: (next) => {
      const updated = typeof next === 'function' ? (next as Updater<T>)(state) : next;
      if (updated === state) return;
      state = updated;
      for (const fn of listeners) fn(state);
    },
    subscribe: (fn) => {
      listeners.add(fn);
      fn(state);
      return () => { listeners.delete(fn); };
    },
  };
}
